export interface WireframeRect {
  top: number
  left: number
  width: number
  height: number
}

export interface WireframeLabel {
  type: string
  name?: string | null
  id?: string | null
  fieldName?: string | null
  meta?: Record<string, any>
}

export interface WireframeElementInfo {
  id: string
  parentId?: string | null
  rect: WireframeRect
  label: WireframeLabel
  meta?: Record<string, any>
}

export interface WireframeGroup {
  id: string
  elementIds: string[]
  parentId: string | null
  childIds: string[]
  rect: WireframeRect
  labels: WireframeLabel[]
  depth: number
  visible: boolean
  open: boolean
  labelStackIndex: number
}

export interface BuildWireframeGroupsOptions {
  maxDepth?: number
  openGroupIds?: Iterable<string>
}

const DEFAULT_MAX_DEPTH = 3

export function buildWireframeGroups(elements: WireframeElementInfo[], options: BuildWireframeGroupsOptions = {}): WireframeGroup[] {
  const maxDepth = options.maxDepth ?? DEFAULT_MAX_DEPTH
  const openGroupIds = new Set(options.openGroupIds ?? [])
  const elementMap = new Map(elements.map(element => [element.id, element]))
  const groupIdByElementId = new Map<string, string>()
  const groupById = new Map<string, WireframeGroup>()

  for (const element of elements) {
    const groupId = findEqualRectGroupRoot(element, elementMap)
    groupIdByElementId.set(element.id, groupId)

    let group = groupById.get(groupId)
    if (!group) {
      group = {
        id: groupId,
        elementIds: [],
        parentId: null,
        childIds: [],
        rect: { ...elementMap.get(groupId).rect },
        labels: [],
        depth: 0,
        visible: false,
        open: openGroupIds.has(groupId),
        labelStackIndex: 0
      }
      groupById.set(groupId, group)
    }

    group.elementIds.push(element.id)
    group.labels.push(element.label)
  }

  for (const group of groupById.values()) {
    const rootElement = elementMap.get(group.id)
    let parentElementId = rootElement?.parentId ?? null

    while (parentElementId) {
      const parentGroupId = groupIdByElementId.get(parentElementId)
      if (parentGroupId && parentGroupId !== group.id) {
        group.parentId = parentGroupId
        groupById.get(parentGroupId)?.childIds.push(group.id)
        break
      }

      parentElementId = elementMap.get(parentElementId)?.parentId ?? null
    }
  }

  const groups = Array.from(groupById.values())
  const depthMemo = new Map<string, number>()

  for (const group of groups) {
    group.depth = resolveDepth(group, groupById, depthMemo)
    group.visible = isVisible(group, groupById, openGroupIds, maxDepth)
  }

  hideSameOriginOpenAncestors(groups, groupById, openGroupIds)
  assignLabelStackIndexes(groups)

  return groups.sort((a, b) => a.depth - b.depth || a.rect.top - b.rect.top || a.rect.left - b.rect.left)
}

function findEqualRectGroupRoot(element: WireframeElementInfo, elementMap: Map<string, WireframeElementInfo>) {
  let root = element
  let parent = element.parentId ? elementMap.get(element.parentId) : null

  while (parent && sameRect(root.rect, parent.rect)) {
    root = parent
    parent = parent.parentId ? elementMap.get(parent.parentId) : null
  }

  return root.id
}

function resolveDepth(group: WireframeGroup, groupById: Map<string, WireframeGroup>, memo: Map<string, number>) {
  const memoized = memo.get(group.id)
  if (memoized != null) {
    return memoized
  }

  const parent = group.parentId ? groupById.get(group.parentId) : null
  const depth = parent ? resolveDepth(parent, groupById, memo) + 1 : 0
  memo.set(group.id, depth)
  return depth
}

function isVisible(group: WireframeGroup, groupById: Map<string, WireframeGroup>, openGroupIds: Set<string>, maxDepth: number) {
  if (group.depth <= maxDepth) {
    return true
  }

  const parent = group.parentId ? groupById.get(group.parentId) : null
  if (!parent || !openGroupIds.has(parent.id)) {
    return false
  }

  return isVisible(parent, groupById, openGroupIds, maxDepth)
}

function hideSameOriginOpenAncestors(groups: WireframeGroup[], groupById: Map<string, WireframeGroup>, openGroupIds: Set<string>) {
  for (const group of groups) {
    if (!group.visible || !openGroupIds.has(group.id)) {
      continue
    }

    const descendant = findVisibleOpenDescendantWithSameOrigin(group, group.rect, groupById, openGroupIds)
    if (descendant) {
      group.visible = false
    }
  }
}

function findVisibleOpenDescendantWithSameOrigin(group: WireframeGroup, origin: WireframeRect, groupById: Map<string, WireframeGroup>, openGroupIds: Set<string>) {
  const children = group.childIds.map(childId => groupById.get(childId)).filter(Boolean) as WireframeGroup[]

  for (const child of children) {
    if (!child.visible) {
      continue
    }

    if (sameOrigin(origin, child.rect)) {
      return child
    }

    if (openGroupIds.has(child.id) && findVisibleOpenDescendantWithSameOrigin(child, origin, groupById, openGroupIds)) {
      return child
    }
  }

  return null
}

function assignLabelStackIndexes(groups: WireframeGroup[]) {
  const stackCounts = new Map<string, number>()

  for (const group of groups.filter(group => group.visible)) {
    const key = `${Math.round(group.rect.left)}:${Math.round(group.rect.top)}`
    const stackIndex = stackCounts.get(key) ?? 0
    group.labelStackIndex = stackIndex
    stackCounts.set(key, stackIndex + 1)
  }
}

function sameRect(a: WireframeRect, b: WireframeRect) {
  return Math.round(a.top) === Math.round(b.top)
    && Math.round(a.left) === Math.round(b.left)
    && Math.round(a.width) === Math.round(b.width)
    && Math.round(a.height) === Math.round(b.height)
}

function sameOrigin(a: WireframeRect, b: WireframeRect) {
  return Math.round(a.top) === Math.round(b.top)
    && Math.round(a.left) === Math.round(b.left)
}
